import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getFilterOptionsWithCounts } from "api/stockFilterApi"
import { useMemo } from "react"
import { some } from "lodash"

export const VEHICLE_FILTER_OPTIONS_QUERY_KEY = "vehicleFilterOptions"

export const useVehicleFilterOptions = () => {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: [VEHICLE_FILTER_OPTIONS_QUERY_KEY],
    queryFn: () => getFilterOptionsWithCounts(),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    keepPreviousData: true,
    initialData: () => {
      // check if queryClient already has cached data
      const cached = queryClient.getQueryData([
        VEHICLE_FILTER_OPTIONS_QUERY_KEY,
      ])
      return cached ?? undefined
    },
  })
}

export const useFilteredVehicleOptions = (params = {}) => {
  const { data: filterData, isLoading, error } = useVehicleFilterOptions()

  const isShowCount = params.isShowCount ?? false
  const isAll = params.isAll ?? false

  // Helper: add label with count
  const withCountLabel = (items = []) =>
    items.map(item => ({
      ...item,
      label: isShowCount
        ? `${item?.name ?? ""} (${item?.total_stocks ?? 0})`
        : item?.name ?? "",
    }))

  // Base data
  const brandsData = withCountLabel(filterData?.data?.brands ?? [])
  const modelsData = withCountLabel(filterData?.data?.models ?? [])
  const packagesData = withCountLabel(filterData?.data?.packages ?? [])
  const engineCodesData = withCountLabel(filterData?.data?.engine_codes ?? [])
  const chassisCodesData = withCountLabel(filterData?.data?.chassis_codes ?? [])
  const engineCCsData = withCountLabel(filterData?.data?.engine_ccs ?? [])
  const featuresData = withCountLabel(filterData?.data?.features ?? [])
  const yearsData = withCountLabel(filterData?.data?.years ?? [])
  const exteriorColorsData = withCountLabel(filterData?.data?.exterior_colors ?? [])
  const interiorColorsData = withCountLabel(filterData?.data?.interior_colors ?? [])
  const statusesData = withCountLabel(filterData?.data?.statuses ?? [])
  const auctionGradesData = withCountLabel(filterData?.data?.auction_grades ?? [])
  const transmissionsData = withCountLabel(filterData?.data?.transmissions ?? [])
  const engineTypesData = withCountLabel(filterData?.data?.engine_types ?? [])
  const fuelTypesData = withCountLabel(filterData?.data?.fuel_types ?? [])
  const keyTypesData = withCountLabel(filterData?.data?.key_types ?? [])
  const locationsData = withCountLabel(filterData?.data?.locations ?? [])
  const platformsData = withCountLabel(filterData?.data?.social_platforms ?? [])
  const importersData = withCountLabel(filterData?.data?.importers ?? [])

  // Filtered models
  const filteredModels = useMemo(() => {
    if (isAll) return modelsData
    if (!params.brand) return []
    return modelsData.filter(model => model.brand?.slug === params.brand)
  }, [params.brand, modelsData, isAll])

  // Filtered packages
  const filteredPackages = useMemo(() => {
    if (isAll) return packagesData
    if (!params.model) return []
    return packagesData.filter(pkg =>
      pkg.models?.some(m => m.slug === params.model)
    )
  }, [params.model, packagesData, isAll])

  // Filtered engine codes
  const filteredEngineCodes = useMemo(() => {
    if (isAll) return engineCodesData
    if (!params.model) return []
    return engineCodesData.filter(code =>
      code.models?.some(m => m.slug === params.model)
    )
  }, [params.model, engineCodesData, isAll])

  // Filtered chassis codes
  const filteredChassisCodes = useMemo(() => {
    if (isAll) return chassisCodesData
    if (!params.model) return []
    return chassisCodesData.filter(ch =>
      ch.models?.some(m => m.slug === params.model)
    )
  }, [params.model, chassisCodesData, isAll])

  // Filtered engine CCs
  const filteredEngineCCs = useMemo(() => {
    if (isAll) return engineCCsData
    if (!params.model) return []
    return engineCCsData.filter(cc =>
      cc.models?.some(m => m.slug === params.model)
    )
  }, [params.model, engineCCsData, isAll])

  // Filtered features
  const filteredFeatures = useMemo(() => {
    if (!params.model) return []
    return featuresData.filter(feature =>
      some(feature.models, { slug: params.model })
    )
  }, [params.model, featuresData])

  // Showrooms (special label)
  const showroomData = (filterData?.data?.showrooms ?? []).map(item => ({
    ...item,
    label: isShowCount
      ? `${item?.short_name ?? ""} - ${item?.name ?? ""} (${item?.total_stocks ?? 0})`
      : `${item?.short_name ?? ""} - ${item?.name ?? ""}`,
  }))

  return {
    isLoading,
    error,
    brands: brandsData,
    models: filteredModels,
    packages: filteredPackages,
    engineCodes: filteredEngineCodes,
    showrooms: showroomData,
    chassisCodes: filteredChassisCodes,
    engineCCs: filteredEngineCCs,
    features: filteredFeatures,
    years: yearsData,
    exteriorColors: exteriorColorsData,
    interiorColors: interiorColorsData,
    statuses: statusesData,
    auctionGrades: auctionGradesData,
    transmissions: transmissionsData,
    engineTypes: engineTypesData,
    fuelTypes: fuelTypesData ,
    keyTypes: keyTypesData ,
    locations: locationsData ,
    platforms: platformsData ,
    importers: importersData ,
    raw: filterData?.data ?? {},
  }
}
